/**
 * @fileoverview Base repository class for PocketBase collections
 */

import type PocketBase from 'pocketbase';
import type { ListResult } from 'pocketbase';
import type { 
  QueryOptions, 
  BaseRecord 
} from '../../types/common/PocketBaseTypes';
import { pb } from '../../helpers/pocketbase_helper';

/**
 * Custom error class for repository operations
 */
export class RepositoryError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'RepositoryError';
  }
}

/**
 * Abstract base class for all repository implementations
 * Provides standard CRUD operations using PocketBase generics
 */
export abstract class DataRepository<T extends BaseRecord> {
  protected collectionName: string;
  protected pb: PocketBase;

  constructor(collectionName: string) {
    this.collectionName = collectionName;
    this.pb = pb;
  }

  /**
   * Get paginated list of records using PocketBase native generics
   */
  async getList(
    page: number, 
    pageSize: number, 
    options: QueryOptions = {}
  ): Promise<ListResult<T>> {
    return this.pb.collection(this.collectionName).getList<T>(page, pageSize, options);
  }

  /**
   * Get all records without pagination
   */
  async getAll(options: QueryOptions = {}): Promise<T[]> {
    return this.pb.collection(this.collectionName).getFullList<T>(options);
  }

  /**
   * Get single record by ID
   */
  async getById(id: string, options: QueryOptions = {}): Promise<T> {
    return this.pb.collection(this.collectionName).getOne<T>(id, options);
  }

  /**
   * Create new record
   */
  async create(data: Partial<T>): Promise<T> {
    return this.pb.collection(this.collectionName).create<T>(data);
  }

  /**
   * Update existing record
   */
  async update(id: string, data: Partial<T>): Promise<T> {
    return this.pb.collection(this.collectionName).update<T>(id, data);
  }

  /**
   * Delete record (hard delete)
   */
  async delete(id: string): Promise<boolean> {
    await this.pb.collection(this.collectionName).delete(id);
    return true;
  }

  /**
   * Find first record matching filter
   * Returns null if not found instead of throwing error
   */
  async findFirst(filter: string, options: QueryOptions = {}): Promise<T | null> {
    try {
      return await this.pb.collection(this.collectionName).getFirstListItem<T>(filter, options);
    } catch (error: any) {
      // PocketBase throws error when no record found
      if (error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Check if record exists by ID
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error: any) {
      if (error.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Count total records matching filter
   */
  async count(filter?: string): Promise<number> {
    const options: QueryOptions = {};
    if (filter) {
      options.filter = filter;
    }
    
    // Get first page to get total count
    const result = await this.getList(1, 1, options);
    return result.totalItems;
  }

  /**
   * Get collection name (useful for debugging)
   */
  getCollectionName(): string {
    return this.collectionName;
  }
}
